<template>
  <section
    v-if="hasContent"
    ref="sectionRoot"
    class="ca-testimonial-masonry"
    :aria-label="content.title || content.label || content.listLabel"
    :aria-busy="content.loading || undefined"
  >
    <div class="ca-bullet-container ca-testimonial-masonry__container">
      <CaModuleMessage
        class="ca-testimonial-masonry__message"
        :label="content.label"
        :title="content.loading ? '' : content.title"
        :title-heading-type="content.titleHeadingType"
        :description="content.description"
        :description-heading-type="content.descriptionHeadingType"
        size="M"
      />

      <div
        v-if="isDesktop"
        class="ca-testimonial-masonry__columns"
        role="list"
        :aria-label="content.listLabel"
      >
        <div
          v-for="(column, columnIndex) in content.columns"
          :key="column.id ?? columnIndex"
          class="ca-testimonial-masonry__column"
          role="presentation"
        >
          <CaTestimonialCard
            v-for="(card, cardIndex) in column.cards"
            :key="card.id ?? cardIndex"
            :card="card"
            role="listitem"
            :aria-label="testimonialLabel(desktopIndex(columnIndex, cardIndex), card)"
            @video-play="
              emitVideoPlay(card, desktopIndex(columnIndex, cardIndex))
            "
          />
        </div>
      </div>

      <div v-else class="ca-testimonial-masonry__carousel">
        <div
          ref="carouselTrack"
          class="ca-testimonial-masonry__track"
          :class="{
            'ca-testimonial-masonry__track--peek': flatCards.length > 1,
          }"
          role="list"
          :aria-label="content.listLabel"
          tabindex="0"
          @scroll.passive="handleCarouselScroll"
          @keydown.left.prevent="movePage(-1)"
          @keydown.right.prevent="movePage(1)"
        >
          <div
            v-for="(card, index) in flatCards"
            :key="card.id ?? index"
            :ref="(element) => setSlideRef(element, index)"
            class="ca-testimonial-masonry__slide"
            role="listitem"
            :aria-label="testimonialLabel(index, card)"
          >
            <CaTestimonialCard
              :card="card"
              @video-play="emitVideoPlay(card, index)"
            />
          </div>
        </div>

        <CaPageControl
          v-if="pageStarts.length > 1"
          class="ca-testimonial-masonry__page-control"
          :count="pageStarts.length"
          :model-value="currentPage"
          @update:model-value="goToPage"
        />

        <p class="ca-testimonial-masonry__status" role="status" aria-live="polite">
          {{ carouselStatus }}
        </p>
      </div>
    </div>
  </section>
</template>

<script setup>
import {
  computed,
  nextTick,
  onBeforeUnmount,
  onBeforeUpdate,
  onMounted,
  ref,
  watch,
} from "vue";
import CaPageControl from "../../base/CaPageControl.vue";
import CaModuleMessage from "../../base/moduleMessage/CaModuleMessage.vue";
import CaTestimonialCard from "./CaTestimonialCard.vue";

defineOptions({
  name: "CaTestimonialMasonry",
});

const props = defineProps({
  model: {
    type: Object,
    default: null,
  },
});

const emit = defineEmits(["video-play"]);
const sectionRoot = ref(null);
const carouselTrack = ref(null);
const slideElements = ref([]);
const viewportWidth = ref(0);
const currentPage = ref(0);
const reducedMotion = ref(false);
const sectionVisible = ref(false);

let intersectionObserver;
let carouselResizeObserver;
let motionQuery;
let carouselFrame;
let parallaxFrame;

const content = computed(() => {
  const source = props.model ?? {};
  const sourceColumns = Array.isArray(source.columns) ? source.columns : [];
  const sourceCards = Array.isArray(source.cards) ? source.cards.slice(0, 9) : [];
  const columns = Array.from({ length: 3 }, (_, index) => {
    const sourceColumn = sourceColumns[index];
    const cards = Array.isArray(sourceColumn)
      ? sourceColumn
      : Array.isArray(sourceColumn?.cards)
        ? sourceColumn.cards
        : [];

    return {
      id: sourceColumn?.id,
      cards: cards.slice(0, 3),
    };
  });

  if (!sourceColumns.length) {
    sourceCards.forEach((card, index) => {
      columns[index % columns.length].cards.push(card);
    });
  }

  return {
    label: readText(source.label),
    title: readText(source.title),
    titleHeadingType: source.titleHeadingType || "H2",
    description: readText(source.description),
    descriptionHeadingType: source.descriptionHeadingType || "Paragraph",
    listLabel: readText(source.listLabel) || "Lista de testemunhos",
    loading: source.loading === true,
    columns,
    orderedCards: sourceColumns.length
      ? columns.flatMap((column) => column.cards)
      : sourceCards,
  };
});

const flatCards = computed(() => content.value.orderedCards);
const hasContent = computed(
  () =>
    Boolean(props.model) &&
    (content.value.loading || content.value.title || flatCards.value.length),
);
const isDesktop = computed(() => viewportWidth.value >= 1024);
const cardsPerView = computed(() => (viewportWidth.value >= 768 ? 2 : 1));
const pageStarts = computed(() => {
  const total = flatCards.value.length;
  const perView = cardsPerView.value;

  if (total <= perView) return [0];

  const starts = [];
  const lastStart = total - perView;
  for (let index = 0; index <= lastStart; index += 1) {
    starts.push(index);
  }

  return starts;
});
const carouselStatus = computed(() => {
  if (!flatCards.value.length) return "";

  const start = pageStarts.value[currentPage.value] ?? 0;
  const end = Math.min(start + cardsPerView.value, flatCards.value.length);
  const position = start === 0
    ? " Início da lista."
    : end === flatCards.value.length
      ? " Fim da lista."
      : "";

  return start + 1 === end
    ? `Testemunho ${end} de ${flatCards.value.length}.${position}`
    : `Testemunhos ${start + 1} a ${end} de ${flatCards.value.length}.${position}`;
});

onBeforeUpdate(() => {
  carouselResizeObserver?.disconnect();
  slideElements.value = [];
});

onMounted(() => {
  viewportWidth.value = window.innerWidth;

  motionQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
  reducedMotion.value = motionQuery.matches;
  motionQuery.addEventListener?.("change", handleMotionPreference);

  intersectionObserver = new IntersectionObserver(([entry]) => {
    sectionVisible.value = entry.isIntersecting;
    if (entry.isIntersecting) {
      scheduleParallax();
    } else {
      resetParallax();
    }
  });
  if (sectionRoot.value) intersectionObserver.observe(sectionRoot.value);

  carouselResizeObserver = new ResizeObserver(() => {
    if (isDesktop.value) return;
    updateCarouselHeight();
  });
  slideElements.value.forEach((slide) => {
    observeSlideCard(slide);
  });
  updateCarouselHeight();

  window.addEventListener("scroll", scheduleParallax, { passive: true });
  window.addEventListener("resize", handleViewportResize, { passive: true });
});

onBeforeUnmount(() => {
  intersectionObserver?.disconnect();
  carouselResizeObserver?.disconnect();
  motionQuery?.removeEventListener?.("change", handleMotionPreference);
  window.removeEventListener("scroll", scheduleParallax);
  window.removeEventListener("resize", handleViewportResize);
  window.cancelAnimationFrame(carouselFrame);
  window.cancelAnimationFrame(parallaxFrame);
});

watch([pageStarts, isDesktop], () => {
  currentPage.value = Math.min(currentPage.value, pageStarts.value.length - 1);
  nextTick(() => {
    if (!isDesktop.value) scrollToCurrentPage("auto");
  });
});

watch([cardsPerView, flatCards], updateCarouselHeight, { flush: "post" });

function readText(value) {
  return String(value ?? "");
}

function desktopIndex(columnIndex, cardIndex) {
  const card = content.value.columns[columnIndex]?.cards[cardIndex];
  const orderedIndex = flatCards.value.indexOf(card);

  if (orderedIndex >= 0) return orderedIndex;

  return content.value.columns
    .slice(0, columnIndex)
    .reduce((total, column) => total + column.cards.length, cardIndex);
}

function testimonialLabel(index, card) {
  const name = readText(card?.name);
  return `Testemunho ${index + 1} de ${flatCards.value.length}${name ? `. ${name}` : ""}`;
}

function emitVideoPlay(card, index) {
  emit("video-play", { card, index });
}

function setSlideRef(element, index) {
  if (!element) return;

  slideElements.value[index] = element;
  nextTick(() => {
    observeSlideCard(element);
    updateCarouselHeight();
  });
}

function observeSlideCard(slide) {
  const card = slide?.querySelector(".ca-testimonial-card");
  if (card) carouselResizeObserver?.observe(card);
}

function goToPage(pageIndex) {
  const nextPage = Math.max(
    0,
    Math.min(Number(pageIndex), pageStarts.value.length - 1),
  );

  currentPage.value = nextPage;
  scrollToCurrentPage(reducedMotion.value ? "auto" : "smooth");
}

function movePage(direction) {
  goToPage(currentPage.value + direction);
}

function scrollToCurrentPage(behavior) {
  const start = pageStarts.value[currentPage.value];
  const slide = slideElements.value[start];
  if (!carouselTrack.value || !slide) return;

  carouselTrack.value.scrollTo({ left: pageScrollLeft(currentPage.value), behavior });
}

function updateCarouselHeight() {
  setCarouselHeight(
    Math.max(
      0,
      ...slideElements.value.map((slide) => slide?.scrollHeight ?? 0),
    ),
  );
}

function setCarouselHeight(nextHeight) {
  const track = carouselTrack.value;

  if (track && nextHeight && track.style.height !== `${nextHeight}px`) {
    track.style.height = `${nextHeight}px`;
  }
}

function pageScrollLeft(pageIndex) {
  const track = carouselTrack.value;
  const start = pageStarts.value[pageIndex];
  const slide = slideElements.value[start];
  if (!track || !slide) return 0;

  const maxScrollLeft = Math.max(0, track.scrollWidth - track.clientWidth);
  return Math.min(
    maxScrollLeft,
    Math.max(0, slide.offsetLeft - track.offsetLeft),
  );
}

function handleCarouselScroll() {
  const track = carouselTrack.value;
  if (!track) return;

  if (carouselFrame) return;

  carouselFrame = window.requestAnimationFrame(() => {
    carouselFrame = undefined;
    const currentScrollLeft = track.scrollLeft;
    let nearestPage = 0;
    let nearestDistance = Number.POSITIVE_INFINITY;

    pageStarts.value.forEach((_, pageIndex) => {
      const distance = Math.abs(pageScrollLeft(pageIndex) - currentScrollLeft);
      if (distance < nearestDistance) {
        nearestDistance = distance;
        nearestPage = pageIndex;
      }
    });

    currentPage.value = nearestPage;
  });
}

function handleMotionPreference(event) {
  reducedMotion.value = event.matches;
  if (event.matches) {
    resetParallax();
  } else {
    scheduleParallax();
  }
}

function handleViewportResize() {
  viewportWidth.value = window.innerWidth;
  if (isDesktop.value) {
    scheduleParallax();
  } else {
    resetParallax();
  }
}

function scheduleParallax() {
  if (!sectionVisible.value || reducedMotion.value || !isDesktop.value) return;
  if (parallaxFrame) return;

  parallaxFrame = window.requestAnimationFrame(() => {
    parallaxFrame = undefined;
    updateParallax();
  });
}

function updateParallax() {
  const root = sectionRoot.value;
  if (!root) return;

  const bounds = root.getBoundingClientRect();
  const viewportHeight = window.innerHeight;
  const progress = Math.max(
    -0.5,
    Math.min(
      0.5,
      (viewportHeight / 2 - (bounds.top + bounds.height / 2)) /
        (viewportHeight + bounds.height),
    ),
  );

  root.style.setProperty("--testimonial-shift-left", `${progress * 180}px`);
  root.style.setProperty("--testimonial-shift-center", `${progress * -120}px`);
  root.style.setProperty("--testimonial-shift-right", `${progress * 150}px`);
}

function resetParallax() {
  const root = sectionRoot.value;
  if (!root) return;

  root.style.setProperty("--testimonial-shift-left", "0px");
  root.style.setProperty("--testimonial-shift-center", "0px");
  root.style.setProperty("--testimonial-shift-right", "0px");
}
</script>

<style scoped lang="scss">
.ca-testimonial-masonry {
  --testimonial-shift-left: 0px;
  --testimonial-shift-center: 0px;
  --testimonial-shift-right: 0px;

  width: 100%;
  padding-block: var(--size-8);
  overflow: hidden;

  @include ca-bp("sm") {
    padding-block: var(--size-9);
  }

  @include ca-bp("md") {
    padding-block: var(--size-11);
  }
}

.ca-testimonial-masonry__container,
.ca-testimonial-masonry__carousel {
  display: flex;
  flex-direction: column;
  width: 100%;
}

.ca-testimonial-masonry__container {
  gap: var(--size-8);
}

.ca-testimonial-masonry__message {
  width: 100%;
}

.ca-testimonial-masonry__columns {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  align-items: start;
  gap: var(--size-5);
  width: 100%;
}

.ca-testimonial-masonry__column {
  --testimonial-column-offset: 0px;
  --testimonial-column-shift: 0px;

  display: flex;
  flex-direction: column;
  gap: var(--size-5);
  min-width: 0;
  transform: translate3d(
    0,
    calc(var(--testimonial-column-offset) + var(--testimonial-column-shift)),
    0
  );
  will-change: transform;
}

.ca-testimonial-masonry__column:first-child,
.ca-testimonial-masonry__column:last-child {
  padding-block: var(--size-9);
}

.ca-testimonial-masonry__column:nth-child(1) {
  --testimonial-column-shift: var(--testimonial-shift-left);
}

.ca-testimonial-masonry__column:nth-child(2) {
  --testimonial-column-shift: var(--testimonial-shift-center);

  @include ca-bp("md") {
    --testimonial-column-offset: calc(-1 * var(--size-5));
  }
}

.ca-testimonial-masonry__column:nth-child(3) {
  --testimonial-column-shift: var(--testimonial-shift-right);

  @include ca-bp("md") {
    --testimonial-column-offset: var(--size-5);
  }
}

.ca-testimonial-masonry__carousel {
  align-items: center;
  gap: var(--size-5);
  min-width: 0;
}

.ca-testimonial-masonry__track {
  display: flex;
  align-items: flex-start;
  gap: var(--size-4);
  width: 100%;
  overflow-x: auto;
  overflow-y: hidden;
  overscroll-behavior-x: contain;
  scroll-behavior: smooth;
  scroll-snap-type: x mandatory;
  scrollbar-width: none;
}

.ca-testimonial-masonry__track::-webkit-scrollbar {
  display: none;
}

.ca-testimonial-masonry__track:focus-visible {
  outline: 2px solid var(--ca-color-outline-focus-only);
  outline-offset: 4px;
}

.ca-testimonial-masonry__slide {
  width: 100%;
  flex: 0 0 100%;
  scroll-snap-align: start;

  @include ca-bp("sm") {
    flex-basis: calc((100% - var(--size-4)) / 2);
  }
}

@media (max-width: 1023px) {
  .ca-testimonial-masonry__track--peek {
    width: calc(100% + var(--size-5) + var(--size-5));
    align-self: flex-start;
    margin-left: calc(-1 * var(--size-5));
    scroll-padding-left: var(--size-5);
  }

  .ca-testimonial-masonry__track--peek
    .ca-testimonial-masonry__slide:first-child {
    margin-left: var(--size-5);
  }

  .ca-testimonial-masonry__track--peek
    .ca-testimonial-masonry__slide:last-child {
    margin-right: var(--size-5);
  }
}

@media (max-width: 767px) {
  .ca-testimonial-masonry__track--peek .ca-testimonial-masonry__slide {
    flex-basis: calc(100% - var(--size-6) - var(--size-4));
  }
}

@media (min-width: 768px) and (max-width: 1023px) {
  .ca-testimonial-masonry__track--peek .ca-testimonial-masonry__slide {
    flex-basis: calc((100% - var(--size-6) - var(--size-6)) / 2);
  }
}

.ca-testimonial-masonry__page-control {
  display: flex;
  align-items: center;
  gap: var(--size-3);
  padding: var(--size-3);
  border-radius: 100px;
  background-color: var(--ca-color-neutrals-inverted);
  box-shadow: 0 2px 6px rgb(0 0 0 / 10%);
}

.ca-testimonial-masonry__page-control :deep(.ca-page-control__dot) {
  background-color: var(--ca-color-neutrals-on-background-low-30);
}

.ca-testimonial-masonry__page-control
  :deep(.ca-page-control__dot--active) {
  background-color: var(--ca-color-theme-primary);
}

.ca-testimonial-masonry__status {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

@media (prefers-reduced-motion: reduce) {
  .ca-testimonial-masonry__column:nth-child(1),
  .ca-testimonial-masonry__column:nth-child(2),
  .ca-testimonial-masonry__column:nth-child(3) {
    transform: translate3d(0, var(--testimonial-column-offset), 0);
    will-change: auto;
    transition: none;
  }

  .ca-testimonial-masonry__track {
    scroll-behavior: auto;
    transition: none;
  }
}
</style>